// Client mirror of api/src/lib/permissions.js. The API remains the access authority.
export const ROLES = Object.freeze(['viewer', 'editor', 'project_admin']);
export const CAPABILITIES = Object.freeze([
  'task:read', 'task:write', 'task:delete', 'comment:write', 'member:manage',
  'settings:manage', 'statuses:manage', 'knowledge:write', 'project:delete',
  'project:admin',
]);
const editor = ['task:read', 'task:write', 'task:delete', 'comment:write', 'knowledge:write'];
const grants = {
  viewer: ['task:read'],
  editor,
  project_admin: [...editor, 'member:manage', 'settings:manage', 'statuses:manage', 'project:admin'],
};

// System-wide admin flag off GET /auth/me. Strict on purpose: anything but a real
// true/1 (a missing user, '1', 'true') is not an admin.
export function isSystemAdmin(me) {
  return !!me && (me.is_admin === true || me.is_admin === 1);
}

export function can(project, me, cap) {
  if (!project || !me || !CAPABILITIES.includes(cap)) return false;
  // Prefer the current server decision when a detail response supplies it.
  if (Array.isArray(project.capabilities)) return project.capabilities.includes(cap);
  if (isSystemAdmin(me)) return true;
  if (me.id != null && project.user_id != null && String(me.id) === String(project.user_id)) return true;
  return Object.hasOwn(grants, project.member_role) && grants[project.member_role].includes(cap);
}

// Display vocabulary shared by every surface that names a role. `owner` is implicit
// (projects.user_id) and `admin` is the system-wide flag — neither is assignable.
export const ROLE_LABELS = Object.freeze({
  viewer: 'Viewer',
  editor: 'Editor',
  project_admin: 'Project Admin',
  owner: 'Owner',
  admin: 'System Admin',
});
export const ROLE_DESCRIPTIONS = Object.freeze({
  viewer: 'Can view tasks, comments and members. Cannot change anything.',
  editor: 'Can create, edit and delete tasks, and comment.',
  project_admin: 'Everything an editor can, plus manage members, settings and workflow.',
  owner: 'Full access, including deleting the project.',
});

export function roleLabel(role) {
  return Object.hasOwn(ROLE_LABELS, role) ? ROLE_LABELS[role] : String(role || '');
}

// The role to show a user for their own access: owner, then system admin, then the
// stored member role. Null when there is nothing to show.
export function effectiveRole(project, me) {
  if (!project || !me) return null;
  if (me.id != null && project.user_id != null && String(me.id) === String(project.user_id)) return 'owner';
  if (isSystemAdmin(me)) return 'admin';
  return Object.hasOwn(grants, project.member_role) ? project.member_role : null;
}

// Page headers render before the project (and so the caller's access) has loaded. Write
// controls there carry `.needs-write` inside an `.access-pending` root, which keeps them
// invisible but laid out; this settles them once access is known — revealed when
// allowed, removed from the DOM when not — so a read-only member never sees them flash.
export function applyWriteGate(root, allowed) {
  if (!root) return;
  if (!allowed) root.querySelectorAll('.needs-write').forEach(el => el.remove());
  root.classList.remove('access-pending');
}

// Detail responses keep access metadata alongside (rather than inside) project.
export function projectWithPermissions(response) {
  if (!response?.project) return null;
  return {
    ...response.project,
    member_role: response.member_role ?? response.project.member_role ?? null,
    ...(Array.isArray(response.capabilities) ? { capabilities: response.capabilities } : {}),
  };
}
